import React, { useState, useEffect, useCallback } from 'react';
import apiHelper from '../../utils/apiHelpers';
import CardBulkList from './CardBulkList';
import CardList from '../findCard/CardList';
import CardFilters from '../card/CardFilters';
import SelectFunctionalTags from '../common/selects/SelectFunctionalTags';
import Loader from '../common/Loader';
import CardsNotFoundList from '../card/CardsNotFoundList';
import cardUtils from '../../utils/cardUtils';
import formatter from '../../utils/formatter';
import TotalAmount from '../common/TotalAmount';
import Button from '../common/Button';
import Input from '../common/Input';
import Alert from '../common/Alert';

const hasAdvancedFilters = (advanced, functionalTags) => {
  const adv = advanced || {};
  return Boolean(
    (adv.colors && adv.colors.length) ||
      (adv.cmc !== null && adv.cmc !== undefined) ||
      (functionalTags && functionalTags.length),
  );
};

const filterInStock = cards =>
  cards.filter(
    card =>
      card.cardPrices &&
      card.cardPrices
        .map(cardPrice => cardPrice.stock)
        .reduce((previous, current) => previous + current, 0) > 0,
  );

const FindBulk = () => {
  const [loading, setLoading] = useState(false);
  const [cards, setCards] = useState([]);
  const [cardsNotFound, setCardsNotFound] = useState([]);
  const [sortedCards, setSortedCards] = useState([]);
  const [cardName, setCardName] = useState('');
  // { where, advanced } — one object from the unified CardFilters.
  const [filters, setFilters] = useState({});
  const [functionalTags, setFunctionalTags] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  // Total matches for the current advanced search, or null for local searches
  // (/card returns a plain array with no count).
  const [totalCards, setTotalCards] = useState(null);
  const [showSorted, setShowSorted] = useState(true);
  const [showCards, setShowCards] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  const searchLocal = useCallback(
    async card_name => {
      // Only `where` goes to /card/bulk — `advanced` is applied by the Scryfall
      // path below and would just bloat the query string here.
      const reqFilters = {
        where: { ...(filters.where || {}), name: card_name },
        limit: 1000,
      };
      const response = await apiHelper.getCardsBulk(reqFilters);
      setCardsNotFound(response.data.cardsNotFound);
      setHasMore(false);
      return response.data.cards.length > 0
        ? filterInStock(response.data.cards)
        : [];
    },
    [filters],
  );

  const searchAdvanced = useCallback(
    async targetPage => {
      const where = filters.where || {};
      const advanced = filters.advanced || {};
      const payload = {
        colors: advanced.colors || undefined,
        colorMode: advanced.colorMode || undefined,
        cmc:
          advanced.cmc !== null && advanced.cmc !== undefined
            ? advanced.cmc
            : undefined,
        cmcComparator: advanced.cmcComparator || undefined,
        type: where.types || undefined,
        functionalTags: functionalTags.length ? functionalTags : undefined,
        conditionRank: where.conditionRank || undefined,
        language: where.language || undefined,
        priceRank: where.priceRank || undefined,
        minPrice: where.minPrice || undefined,
        maxPrice: where.maxPrice || undefined,
        stores: where.stores || undefined,
        page: targetPage,
      };
      const response = await apiHelper.searchDeckBuilderCards(payload);
      setCardsNotFound(response.data.cardsNotFound || []);
      setHasMore(response.data.hasMore);
      setTotalCards(
        typeof response.data.totalCards === 'number'
          ? response.data.totalCards
          : null,
      );
      setPage(targetPage);
      return filterInStock(response.data.cards || []);
    },
    [filters, functionalTags],
  );

  const runSearch = useCallback(
    (card_name, append) => {
      setLoading(true);
      setErrorMessage('');
      const isAdvanced = hasAdvancedFilters(filters.advanced, functionalTags);
      const searchPromise = isAdvanced
        ? searchAdvanced(append ? page + 1 : 1)
        : searchLocal(card_name);
      searchPromise
        .then(newCards => {
          setCards(prevCards => {
            const merged = append ? prevCards.concat(newCards) : newCards;
            return merged.filter(
              (card, index, self) =>
                index === self.findIndex(c => c.id === card.id),
            );
          });
          setLoading(false);
        })
        .catch(error => {
          console.log(error);
          setLoading(false);
          if (error.response) {
            const { status, data } = error.response;
            if (status === 400 && data?.message) {
              setErrorMessage(data.message);
            } else if ([400, 422, 204].includes(status)) {
              setErrorMessage('Error al cargar datos de las cartas');
            } else if (error.request) {
              setErrorMessage('Sin respuesta del Servidor');
            } else {
              setErrorMessage(error.message);
            }
          } else {
            setErrorMessage('Error de conexión con el servidor');
          }
        });
    },
    [filters, functionalTags, page, searchAdvanced, searchLocal],
  );

  useEffect(() => {
    if (
      cardName.length >= 4 ||
      hasAdvancedFilters(filters.advanced, functionalTags)
    ) {
      let currCardName = cardName;
      setTimeout(function () {
        if (currCardName === cardName) runSearch(cardName.trim(), false);
      }, 1000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cardName, filters, functionalTags]);

  useEffect(() => {
    if (cards.length) {
      setSortedCards(cardUtils.groupCardsByStore(cards));
    } else {
      setSortedCards([]);
    }
  }, [cards, filters]);

  const handleChange = e => {
    setCardName(e.target.value.toString());
  };

  const handleChangeFilters = filters => {
    setFilters(filters);
  };

  const handleFunctionalTagsChange = event => {
    setFunctionalTags(event.target.value);
  };

  // CardFilters clears its own state, including colors and mana value; the tag
  // selector lives outside it, so it is cleared here.
  const handleFiltersReset = () => {
    setFunctionalTags([]);
  };

  const loadMore = () => {
    runSearch(cardName.trim(), true);
  };

  return (
    <div className="flex flex-col gap-y-4">
      <h1 className="text-2xl text-center">
        <i className="fas fa-list-ol mr-2"></i>
        Buscar listado de cartas
      </h1>
      <h4>
        Pegá una lista completa de cartas y te mostramos el mejor precio de cada
        una, agrupadas por tienda, para que puedas comprar toda la lista en la
        menor cantidad de pedidos posible.
      </h4>
      <div className="flex flex-col gap-y-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="w-full">
            <Input
              type="textarea"
              name="card_name"
              placeholder={
                `El formato debe ser "[cantidad de unidades] [nombre de carta]" (Formato MTGO) o bien "[nombre de carta]"` +
                `\n` +
                `\nEjemplo de formato 1:` +
                `\n1 Return to Nature` +
                `\n1 Safewright Quest` +
                `\n` +
                `\nEjemplo de formato 2:` +
                `\nWhispers of the Muse` +
                `\nIron Myr`
              }
              value={cardName}
              onChange={handleChange}
            />
          </div>
        </div>
        <div className="w-full">
          <SelectFunctionalTags
            value={functionalTags}
            name="functionalTags"
            placeholder="Etiquetas funcionales (ej: rampa, remoción)"
            onChange={handleFunctionalTagsChange}
          />
        </div>
        <CardFilters
          loadData={handleChangeFilters}
          onReset={handleFiltersReset}
          fields={[
            'condition',
            'priceRank',
            'language',
            'price',
            'types',
            'stores',
            'colors',
            'cmc',
          ]}
        />

        <div className="flex flex-col sm:flex-row gap-3 sm:flex-row gap-4">
          <Button
            className="w-full sm:w-1/3"
            iconClass="fas fa-id-card"
            onClick={() => {
              setShowSorted(false);
              setShowCards(true);
            }}
            disabled={loading}>
            Ver por carta
          </Button>
          <Button
            className="w-full sm:w-1/3"
            iconClass="fas fa-store"
            onClick={() => {
              setShowSorted(true);
              setShowCards(true);
            }}
            disabled={loading}>
            Ver por tienda (cartas)
          </Button>
          <Button
            className="w-full sm:w-1/3"
            iconClass="fas fa-list-alt"
            onClick={() => {
              setShowSorted(true);
              setShowCards(false);
            }}
            disabled={loading}>
            Ver por tienda (lista)
          </Button>
        </div>

        {loading && <Loader />}
        {!loading && !showSorted && (
          <>
            <Alert>{errorMessage}</Alert>
            <TotalAmount cards={cards} />
            <CardList cards={cards} showCards={showCards} />
          </>
        )}
        {!loading && showSorted && (
          <>
            <Alert>{errorMessage}</Alert>
            <TotalAmount cards={cards} />
            {sortedCards.map(cardsByStore => (
              <CardBulkList
                key={cardsByStore.store}
                store={cardsByStore.store}
                cards={cardsByStore.cards}
                showCards={showCards}
              />
            ))}
          </>
        )}

        {!loading && cardsNotFound.length > 0 && (
          <CardsNotFoundList cardsNotFound={cardsNotFound} />
        )}

        {!loading && totalCards !== null && (
          <p className="text-white text-sm">
            {formatter.formatNumber(totalCards)}{' '}
            {totalCards === 1 ? 'resultado' : 'resultados'}
          </p>
        )}
        {!loading && hasMore && (
          <Button iconClass="fas fa-chevron-down" onClick={loadMore}>
            Cargar más resultados
          </Button>
        )}
      </div>
    </div>
  );
};

export default FindBulk;
